'use client'

import type { generateClient } from 'aws-amplify/data'
import type { Schema } from '@app/backend/data/resource'

/**
 * The browser's one doorway to Amplify — and it holds none of Amplify.
 *
 * The SDK (core, auth, data) lives in `./amplify-runtime`, which this file loads with a dynamic
 * import on the first real need: a sign-in, a data call, or a stored session to check. Until then no page carries it. Loaded eagerly from the root layout, it
 * was most of the ~890 KB every marketing page shipped, competing with the font for bandwidth and
 * putting a mobile Lighthouse paint at 5.9s. Next also prefetches the chunks of every linked page,
 * so a static import from /contact or /login would put it back on every page that links them.
 *
 * Nothing may import `./amplify-runtime` statically, or `aws-amplify` at runtime, anywhere but the
 * runtime itself — the repo-shape test checks both, because either undoes the split without a
 * single error.
 */

export type DataClient = ReturnType<typeof generateClient<Schema>>
type AmplifyRuntime = typeof import('./amplify-runtime')

let runtime: Promise<AmplifyRuntime> | null = null
const whenLoaded: ((loaded: AmplifyRuntime) => void)[] = []

/** Load the SDK once, configured, and hand it over. Every auth and data call awaits this. */
export function amplifyRuntime(): Promise<AmplifyRuntime> {
    if (!runtime) {
        runtime = import('./amplify-runtime')
        // Chained here, before the caller's own `await` resumes, so a listener waiting on the load
        // is attached before the sign-in that caused the load can emit its first event.
        void runtime.then((loaded) => { for (const callback of whenLoaded.splice(0)) { callback(loaded) } }, () => {})
    }
    return runtime
}

/**
 * Run `callback` once the SDK has loaded, WITHOUT loading it. For listeners — the session's Hub
 * subscription — that only mean something once the SDK exists: no auth event can fire before the
 * SDK that emits it, so attaching on load misses nothing, and a guest who never needs the SDK
 * never downloads it to listen to silence.
 */
export function onAmplifyRuntimeReady(callback: (loaded: AmplifyRuntime) => void): void {
    if (runtime) { void runtime.then(callback, () => {}); return }
    whenLoaded.push(callback)
}

/**
 * The typed AppSync client, signed as whoever is calling: `userPool` for a signed-in person,
 * `identityPool` for a guest (decided in the runtime, at call time). `lib/api/*` functions take it
 * as an argument, so tests pass a stub instead.
 */
export async function dataClient(): Promise<DataClient> {
    return (await amplifyRuntime()).dataClient()
}

/** Amplify's token namespace, the same whichever storage it writes to. */
const COGNITO_KEY_PREFIX = 'CognitoIdentityServiceProvider'

/**
 * Whether a `storage` event is about the sign-in tokens — another tab signing in, refreshing or
 * signing out. `null` is `localStorage.clear()`, which takes the tokens with it.
 */
export function isSessionStorageKey(key: string | null): boolean {
    return key === null || key.startsWith(COGNITO_KEY_PREFIX)
}

/**
 * Could this visitor be signed in at all? Answered without the SDK, so a guest — most visitors to
 * a marketing page — resolves to guest without downloading it.
 *
 * Both stores are read although tokens live in localStorage today (`Amplify.configure` without
 * `{ ssr: true }`): the store is decided by a flag in another file, and this function's wrong
 * answer is a silent sign-out. techwalk shipped that once — the flag moved tokens to cookies while
 * the check looked only in localStorage, and every session read as a guest. A false "yes" only
 * costs the SDK download this exists to skip, so an unreadable store answers yes.
 */
export function hasStoredSession(): boolean {
    try {
        if (document.cookie.includes(COGNITO_KEY_PREFIX)) { return true }
        for (let index = 0; index < localStorage.length; index++) {
            if (localStorage.key(index)?.startsWith(COGNITO_KEY_PREFIX)) { return true }
        }
        return false
    } catch {
        return true
    }
}
